import * as R from 'ramda';
import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// components
import { Table } from '../../components/table';
import { TitlePanel } from '../../components/title-panel';
import { PageActions } from '../../components/page-actions';
import { ConfirmComponent } from '../../components/confirm';
// features
import Audit2 from '../audit2';
import { AuthWrapper } from '../permission';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// hooks
import { useModalActions } from '../../hooks';
import useReport from '../../hooks/use-report';
import useFixedPopover from '../../hooks/use-mui-fixed-popover';
// icons
import * as I from '../../svgs';
// ui
import { IconWrapper, ListWrapper, ZOrderWrapper } from '../../ui';
// feature compensation-group
import * as A from './actions';
import { RowActions } from './components/row-actions';
import CompensationGroup from './components/compensation-group';
import { tableSettings, columnSettings, FILTER_PARAMS } from './settings';
import { makeSelectItemList, makeSelectDriverGuid, makeSelectVendorGuid } from './selectors';
//////////////////////////////////////////////////

const CompensationGroupList = (props) => {
  const {
    loading,
    openModal,
    totalCount,
    pagination,
    reportList,
    isFleetProfile,
    selectedReport,
    isReadOnlyMode,
    openFixedPopup,
    closeFixedPopup,
    titleSortValues,
    tableTitleFilters,
    getItemListRequest,
    handleCreateOrUpdateCompensationGroup,
  } = props;

  const dispatch = useDispatch();

  const itemList = useSelector(makeSelectItemList);

  const toggle = useCallback(({ guid }) => dispatch(A.toggleDetails(guid)), []);

  const handleTableTitleFilter = useCallback((data) => {
    G.handleTableTitleFilter2(data, A, dispatch);

    getItemListRequest();
  }, []);

  const handleOpenAudit = useCallback((guid, { name }) => {
    const requestOptions = {
      [GC.FIELD_OBJECT_GUID]: guid,
      [GC.FIELD_TYPE]: GC.AUDIT_TYPE_BRANCH_CONFIG,
    };

    const component = <Audit2 requestOptions={requestOptions} />;

    const modal = {
      p: '0',
      component,
      options: {
        withCloseIcon: true,
        backgroundColor: 'transparent',
        title: `${G.getWindowLocale('titles:audit', 'Audit')}: ${name}`,
      },
    };

    openModal(modal);
  }, []);

  const handleRemoveItem = useCallback((guid, { name }) => {
    const modal = {
      component: (
        <ConfirmComponent
          name={name}
          textLocale={G.getWindowLocale('messages:before:remove', 'Are you sure you want to remove')}
        />
      ),
      options: {
        width: 600,
        controlButtons: [
          {
            type: 'button',
            name: G.getWindowLocale('actions:delete', 'Delete'),
            action: () => dispatch(A.removeCompensationGroupRequest(guid)),
          },
        ],
      },
    };

    openModal(modal);
  }, []);

  const handleClickEditIcon = ({ currentTarget }, entity) => {
    openFixedPopup({
      position: 'right',
      el: currentTarget,
      content: (
        <RowActions
          entity={entity}
          isFleetProfile={isFleetProfile}
          closeFixedPopup={closeFixedPopup}
          handleOpenAudit={handleOpenAudit}
          handleRemoveItem={handleRemoveItem}
        />
      ),
    });
  };

  const actionButtons = isReadOnlyMode ? [] : [
    {
      iconName: 'pencil',
      action: handleCreateOrUpdateCompensationGroup,
      title: G.getWindowLocale('actions:edit', 'Edit'),
    },
  ];

  const tableSettingsToUse = G.getTableSettingsWithMaxHeightByConditions({
    reportList,
    selectedReport,
    tableSettings: isFleetProfile ? R.assoc('tableWrapperProps', { m: '2px' }, tableSettings) : tableSettings,
  });

  const elementActionsComponent = (entity) => (
    <IconWrapper px={12} cursor='pointer' onClick={(e) => handleClickEditIcon(e, entity)}>
      {I.threeDots()}
    </IconWrapper>
  );

  const data = {
    toggle,
    loading,
    itemList,
    totalCount,
    pagination,
    actionButtons,
    columnSettings,
    titleSortValues,
    tableTitleFilters,
    handleTableTitleFilter,
    report: selectedReport,
    useTableUiWithBg: true,
    withResizableColumns: true,
    useNewTitleFilterInputs: true,
    callbackData: { isReadOnlyMode },
    tableSettings: tableSettingsToUse,
    allChecked: G.isAllChecked(itemList),
    handleLoadMoreEntities: getItemListRequest,
    renderRightStickedComponent: elementActionsComponent,
    filterProps: R.indexBy(R.prop(GC.FIELD_VALUE), FILTER_PARAMS),
  };

  return <Table {...data} />;
};

const CompensationGroupReport = (props) => {
  const { pl, height, maxHeight, isReadOnlyMode, customTitleComponent } = props;

  const driverGuid = useSelector(makeSelectDriverGuid());
  const vendorGuid = useSelector(makeSelectVendorGuid());

  const { openModal, closeModal } = useModalActions();

  const { openFixedPopup, closeFixedPopup, PopoverComponent } = useFixedPopover();

  const reportProps = {
    type: GC.FLEET_COMPENSATION_GROUP_REPORT,
    ...useReport({ A, type: 'compensationGroup' }),
  };

  const handleCreateOrUpdateCompensationGroup = useCallback((guid) => {
    const title = G.ifElse(
      R.isNotNil(guid),
      G.getEditTitle,
      G.getAddTitle,
    )(['titles:compensation-group', 'Compensation Group']);

    const modal = {
      p: 15,
      component: <CompensationGroup guid={guid} closeModal={closeModal} />,
      options: {
        title,
        width: 1200,
        maxWidth: '95vw',
      },
    };

    openModal(modal);
  }, []);

  const isFleetProfile = R.isNotNil(R.or(driverGuid, vendorGuid));

  return (
    <ListWrapper pl={pl} height={height} maxHeight={maxHeight}>
      <ZOrderWrapper zIndex={2}>
        <TitlePanel
          {...reportProps}
          pt={25}
          pr={15}
          withCount={true}
          noExportable={true}
          hideFilterInfo={true}
          popperWithCount={true}
          useTableUiWithBg={true}
          filterProps={FILTER_PARAMS}
          hiddenRightFilterInfo={false}
          withGlobalSearchFilter={false}
          customTitleComponent={customTitleComponent}
          title={G.getWindowLocale('titles:compensation-group', 'Compensation Group')}
        />
      </ZOrderWrapper>
      <ZOrderWrapper zIndex={1} p='15px 25px'>
        <CompensationGroupList
          {...reportProps}
          openModal={openModal}
          isReadOnlyMode={isReadOnlyMode}
          openFixedPopup={openFixedPopup}
          isFleetProfile={isFleetProfile}
          closeFixedPopup={closeFixedPopup}
          handleCreateOrUpdateCompensationGroup={handleCreateOrUpdateCompensationGroup}
        />
        {PopoverComponent}
      </ZOrderWrapper>
      <AuthWrapper has={G.ifElse(isFleetProfile, '')}>
        <PageActions
          shadowColor={G.getTheme('createButton.shadowColor')}
          mainAction={{
            action: () => handleCreateOrUpdateCompensationGroup(),
            text: G.getWindowLocale('titles:create-compensation-group', 'Create Compensation Group'),
          }}
        />
      </AuthWrapper>
    </ListWrapper>
  );
};

export default CompensationGroupReport;
